import React from "react"
import { Row, Col, Form, Input, Select } from "antd"
import PropTypes from "prop-types"

const BillingAddressForm = ({ form, handleFieldChange, shouldShowValidation, t }) => {
  // common handler factory for first/last name inputs (sanitizes digits and handles paste)
  const createSanitizedNameHandlers = (fieldName) => {
    const sanitize = (value = "") => value.replaceAll(/\d/g, "")
    return {
      onChange: (e) => {
        const sanitized = sanitize(e.target.value)
        handleFieldChange({ target: { name: fieldName, value: sanitized } })
      },
      onPaste: (e) => {
        const pasted = (e.clipboardData || window.clipboardData).getData("text")
        const sanitized = sanitize(pasted)
        e.preventDefault()
        handleFieldChange({ target: { name: fieldName, value: sanitized } })
      }
    }
  }

  return (
    <Row gutter={16} className="mt-m">
      <Col span={12}>
        <Form.Item 
          label={`${t("payment.first_name")} *`}
          validateStatus={shouldShowValidation("billing_details.first_name", !form.billing_details?.first_name) ? "error" : ""}
          help={shouldShowValidation("billing_details.first_name", !form.billing_details?.first_name) ? (t("payment.first_name_required") || "First name is required") : ""}
        >
          <Input
            type="text"
            name="billing_details.first_name"
            value={form.billing_details?.first_name || ""}
            {...createSanitizedNameHandlers("billing_details.first_name")}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.last_name")} *`}
          validateStatus={shouldShowValidation("billing_details.last_name", !form.billing_details?.last_name) ? "error" : ""}
          help={shouldShowValidation("billing_details.last_name", !form.billing_details?.last_name) ? (t("payment.last_name_required") || "Last name is required") : ""}
        >
          <Input
            name="billing_details.last_name"
            value={form.billing_details?.last_name || ""}
            {...createSanitizedNameHandlers("billing_details.last_name")}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.address_line1")} *`}
          validateStatus={shouldShowValidation("billing_details.address.line1", !form.billing_details?.address?.line1) ? "error" : ""}
          help={shouldShowValidation("billing_details.address.line1", !form.billing_details?.address?.line1) ? (t("payment.address_line1_required") || "Address line 1 is required") : ""}
        >
          <Input name="billing_details.address.line1" value={form.billing_details?.address?.line1 || ""} onChange={handleFieldChange} />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item label={t("payment.address_line2")}>
          <Input name="billing_details.address.line2" value={form.billing_details?.address?.line2 || ""} onChange={handleFieldChange} />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.city")} *`}
          validateStatus={shouldShowValidation("billing_details.address.city", !form.billing_details?.address?.city) ? "error" : ""}
          help={shouldShowValidation("billing_details.address.city", !form.billing_details?.address?.city) ? (t("payment.city_required") || "City is required") : ""}
        >
          <Input name="billing_details.address.city" value={form.billing_details?.address?.city || ""} onChange={handleFieldChange} />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.state")} *`}
          validateStatus={shouldShowValidation("billing_details.address.state", !form.billing_details?.address?.state) ? "error" : ""}
          help={shouldShowValidation("billing_details.address.state", !form.billing_details?.address?.state) ? (t("payment.state_required") || "State is required") : ""}
        >
          <Input name="billing_details.address.state" value={form.billing_details?.address?.state || ""} onChange={handleFieldChange} />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.zip")} *`}
          validateStatus={shouldShowValidation("billing_details.address.zip_code", !form.billing_details?.address?.zip_code) ? "error" : ""}
          help={shouldShowValidation("billing_details.address.zip_code", !form.billing_details?.address?.zip_code) ? (t("payment.zip_required") || "Zip code is required") : ""}
        >
          <Input
            name="billing_details.address.zip_code"
            value={form.billing_details?.address?.zip_code || ""}
            onChange={handleFieldChange}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item 
          label={`${t("payment.country")} *`}
          validateStatus={shouldShowValidation("billing_details.address.country", !form.billing_details?.address?.country) ? "error" : ""}
          help={shouldShowValidation("billing_details.address.country", !form.billing_details?.address?.country) ? (t("payment.country_required") || "Country is required") : ""}
        >
          <Select
            name="billing_details.address.country"
            value={form.billing_details?.address?.country || ""}
            onChange={(val) =>
              handleFieldChange({
                target: { name: "billing_details.address.country", value: val }
              })
            }
            showSearch
            placeholder={t("payment.select_country")}
          >
            {require("country-list")
              .getData()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(({ code, name }) => (
                <Select.Option key={code} value={code}>
                  {name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  )
}

BillingAddressForm.propTypes = {
  form: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  shouldShowValidation: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

export default BillingAddressForm
